<template>
  <div class="master-board-charts space-y-6">
    <div class="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <!-- Status Chart -->
      <div class="bg-white rounded-2xl border border-gray-200 shadow-md p-6 xl:col-span-1">
        <h3 class="text-base font-semibold text-gray-900 mb-1">Status</h3>
        <p class="text-xs text-gray-500 mb-4">Task distribution across all sub-boards. Click a segment to view tasks.</p>

        <div v-if="chartLoading" class="flex items-center justify-center py-16">
          <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
        </div>

        <div v-else-if="hasStatusChartData" class="master-board-status-chart-wrap master-board-status-chart-clickable">
          <apexchart type="donut" height="320" :options="statusChartOptions" :series="statusChartSeries"
            @dataPointSelection="onStatusChartSegmentClick" />
        </div>

        <div v-else class="text-center py-16 text-gray-500">
          <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
          </svg>
          <p class="text-sm text-gray-600">No tasks yet</p>
          <p class="text-xs text-gray-400 mt-1">Create tasks on sub-boards to see status breakdown.</p>
        </div>
      </div>

      <!-- Priority Chart -->
      <div class="bg-white rounded-2xl border border-gray-200 shadow-md p-6 xl:col-span-1">
        <h3 class="text-base font-semibold text-gray-900 mb-1">Priority</h3>
        <p class="text-xs text-gray-500 mb-4">Tasks by priority and status. Click a bar to view tasks.</p>

        <div v-if="chartLoading" class="flex items-center justify-center py-16">
          <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
        </div>

        <div v-else-if="hasPriorityChartData" class="master-board-priority-chart-clickable">
          <apexchart type="bar" height="320" :options="priorityChartOptions" :series="priorityChartSeries"
            @dataPointSelection="onPriorityChartBarClick" />
        </div>

        <div v-else class="text-center py-16 text-gray-500">
          <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          <p class="text-sm text-gray-600">No tasks yet</p>
          <p class="text-xs text-gray-400 mt-1">Create tasks on sub-boards to see priority breakdown.</p>
        </div>
      </div>

      <!-- Sub-Boards Chart -->
      <div class="bg-white rounded-2xl border border-gray-200 shadow-md p-6 xl:col-span-1">
        <div class="flex items-start justify-between gap-3 mb-1">
          <div class="min-w-0">
            <h3 class="text-base font-semibold text-gray-900">Sub-Boards</h3>
            <p class="text-xs text-gray-500 mt-1">Tasks by sub-board and status. Click a bar to view tasks.</p>
          </div>
          <div v-if="subBoardChartPageCount > 1" class="flex items-center gap-1 text-xs text-gray-600 flex-shrink-0">
            <button type="button" :disabled="subBoardChartPage <= 1" @click="subBoardChartPrevPage"
              class="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Previous page">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span class="tabular-nums px-1">{{ subBoardChartPage }} of {{ subBoardChartPageCount }}</span>
            <button type="button" :disabled="subBoardChartPage >= subBoardChartPageCount" @click="subBoardChartNextPage"
              class="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Next page">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div v-if="chartLoading" class="flex items-center justify-center py-16 mt-4">
          <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
        </div>

        <div v-else-if="hasSubBoardChartData" class="mt-4 master-board-sub-board-chart-clickable">
          <apexchart type="bar" height="320" :options="subBoardChartOptions" :series="subBoardChartSeries"
            @dataPointSelection="onSubBoardChartBarClick" />
        </div>

        <div v-else class="text-center py-16 text-gray-500 mt-4">
          <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v2" />
          </svg>
          <p class="text-sm text-gray-600">No sub-boards yet</p>
          <p class="text-xs text-gray-400 mt-1">Create sub-boards to see task breakdown by board.</p>
        </div>
      </div>
    </div>

    <!-- Members Chart (full width) -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-md p-6">
      <div class="flex items-start justify-between gap-3 mb-1">
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-gray-900">Members</h3>
          <p class="text-xs text-gray-500 mt-1">Tasks by member and status. Click a bar to view tasks.</p>
        </div>
        <div v-if="memberChartPageCount > 1" class="flex items-center gap-1 text-xs text-gray-600 flex-shrink-0">
          <button type="button" :disabled="memberChartPage <= 1" @click="memberChartPrevPage"
            class="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Previous page">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span class="tabular-nums px-1">{{ memberChartPage }} of {{ memberChartPageCount }}</span>
          <button type="button" :disabled="memberChartPage >= memberChartPageCount" @click="memberChartNextPage"
            class="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Next page">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <div v-if="chartLoading" class="flex items-center justify-center py-16 mt-4">
        <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
      </div>

      <div v-else-if="hasMemberChartData" class="mt-4 master-board-member-chart-clickable">
        <apexchart type="bar" height="360" :options="memberChartOptions" :series="memberChartSeries"
          @dataPointSelection="onMemberChartBarClick" />
      </div>

      <div v-else class="text-center py-16 text-gray-500 mt-4">
        <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <p class="text-sm text-gray-600">No member task data yet</p>
        <p class="text-xs text-gray-400 mt-1">Assign tasks to members on sub-boards to see this chart.</p>
      </div>
    </div>

    <master-board-status-tasks-modal :visible="statusTasksModalVisible" :master-board-id="masterBoardId"
      :chart-status="statusTasksModalBucket" :status-label="statusTasksModalLabel"
      @close="closeStatusTasksModal" />

    <master-board-priority-tasks-modal :visible="priorityTasksModalVisible" :master-board-id="masterBoardId"
      :priority="priorityTasksModalPriority" :priority-label="priorityTasksModalLabel"
      @close="closePriorityTasksModal" />

    <master-board-sub-board-tasks-modal :visible="subBoardTasksModalVisible" :master-board-id="masterBoardId"
      :board-id="subBoardTasksModalBoardId" :board-label="subBoardTasksModalLabel"
      @close="closeSubBoardTasksModal" />

    <master-board-member-tasks-modal :visible="memberTasksModalVisible" :master-board-id="masterBoardId"
      :assignee-user-id="memberTasksModalUserId" :member-label="memberTasksModalLabel"
      @close="closeMemberTasksModal" />
  </div>
</template>

<script>
import gql from 'graphql-tag';
import MasterBoardStatusTasksModal from './MasterBoardStatusTasksModal.vue';
import MasterBoardPriorityTasksModal from './MasterBoardPriorityTasksModal.vue';
import MasterBoardSubBoardTasksModal from './MasterBoardSubBoardTasksModal.vue';
import MasterBoardMemberTasksModal from './MasterBoardMemberTasksModal.vue';

var STATUS_CHART_COLORS = ['#7C3AED', '#3B82F6', '#EF4444', '#14B8A6'];
var STATUS_CHART_LABELS = ['Not Started', 'In Progress', 'Late', 'Completed'];
var STATUS_CHART_BUCKETS = ['not_started', 'in_progress', 'late', 'completed'];
var SUB_BOARDS_PER_CHART_PAGE = 5;
var MEMBERS_PER_CHART_PAGE = 8;

export default {
  name: 'master-board-charts',
  components: {
    MasterBoardStatusTasksModal,
    MasterBoardPriorityTasksModal,
    MasterBoardSubBoardTasksModal,
    MasterBoardMemberTasksModal,
  },
  props: {
    masterBoardId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      chartStats: null,
      chartLoading: false,
      chartError: null,
      subBoardChartPage: 1,
      memberChartPage: 1,
      statusTasksModalVisible: false,
      statusTasksModalBucket: '',
      statusTasksModalLabel: '',
      priorityTasksModalVisible: false,
      priorityTasksModalPriority: '',
      priorityTasksModalLabel: '',
      subBoardTasksModalVisible: false,
      subBoardTasksModalBoardId: '',
      subBoardTasksModalLabel: '',
      memberTasksModalVisible: false,
      memberTasksModalUserId: '',
      memberTasksModalLabel: '',
    };
  },
  computed: {
    statusChartData() {
      if (!this.chartStats || !this.chartStats.status) {
        return null;
      }
      return this.chartStats.status;
    },
    priorityChartRows() {
      if (!this.chartStats || !this.chartStats.priority || !this.chartStats.priority.rows) {
        return [];
      }
      return this.chartStats.priority.rows;
    },
    subBoardChartRows() {
      if (!this.chartStats || !this.chartStats.sub_boards || !this.chartStats.sub_boards.rows) {
        return [];
      }
      return this.chartStats.sub_boards.rows;
    },
    subBoardChartPageCount() {
      var rows = this.subBoardChartRows;
      if (!rows.length) {
        return 0;
      }
      return Math.ceil(rows.length / SUB_BOARDS_PER_CHART_PAGE);
    },
    subBoardChartPageRows() {
      var rows = this.subBoardChartRows;
      var page = this.subBoardChartPage;
      var start = (page - 1) * SUB_BOARDS_PER_CHART_PAGE;
      return rows.slice(start, start + SUB_BOARDS_PER_CHART_PAGE);
    },
    hasSubBoardChartData() {
      return this.subBoardChartRows.length > 0;
    },
    memberChartRows() {
      if (!this.chartStats || !this.chartStats.members || !this.chartStats.members.rows) {
        return [];
      }
      return this.chartStats.members.rows;
    },
    memberChartPageCount() {
      var rows = this.memberChartRows;
      if (!rows.length) {
        return 0;
      }
      return Math.ceil(rows.length / MEMBERS_PER_CHART_PAGE);
    },
    memberChartPageRows() {
      var rows = this.memberChartRows;
      var page = this.memberChartPage;
      var start = (page - 1) * MEMBERS_PER_CHART_PAGE;
      return rows.slice(start, start + MEMBERS_PER_CHART_PAGE);
    },
    hasMemberChartData() {
      var rows = this.memberChartRows;
      if (!rows.length) {
        return false;
      }
      var i;
      for (i = 0; i < rows.length; i++) {
        if (rows[i].total > 0) {
          return true;
        }
      }
      return false;
    },
    hasStatusChartData() {
      const d = this.statusChartData;
      if (!d) {
        return false;
      }
      return (d.total || 0) > 0;
    },
    hasPriorityChartData() {
      const rows = this.priorityChartRows;
      if (!rows || !rows.length) {
        return false;
      }
      var i;
      for (i = 0; i < rows.length; i++) {
        if (rows[i].total > 0) {
          return true;
        }
      }
      return false;
    },
    statusChartSeries() {
      const d = this.statusChartData;
      if (!d) {
        return [];
      }
      return [
        d.not_started || 0,
        d.in_progress || 0,
        d.late || 0,
        d.completed || 0,
      ];
    },
    priorityChartCategories() {
      return this.priorityChartRows.map(function (row) {
        return row.label || row.priority;
      });
    },
    priorityChartSeries() {
      return this.buildStackedStatusSeries(this.priorityChartRows);
    },
    priorityChartMaxY() {
      return this.getStackedChartMaxY(this.priorityChartRows);
    },
    subBoardChartCategories() {
      return this.subBoardChartPageRows.map(function (row) {
        var label = row.label || 'Sub-board';
        if (label.length > 14) {
          return label.substring(0, 12) + '...';
        }
        return label;
      });
    },
    subBoardChartSeries() {
      return this.buildStackedStatusSeries(this.subBoardChartPageRows);
    },
    subBoardChartMaxY() {
      return this.getStackedChartMaxY(this.subBoardChartPageRows);
    },
    memberChartCategories() {
      return this.memberChartPageRows.map(function (row) {
        var label = row.label || 'Member';
        if (label.length > 16) {
          return label.substring(0, 14) + '...';
        }
        return label;
      });
    },
    memberChartSeries() {
      return this.buildStackedStatusSeries(this.memberChartPageRows);
    },
    memberChartMaxY() {
      return this.getStackedChartMaxY(this.memberChartPageRows);
    },
    statusChartOptions() {
      const d = this.statusChartData;
      const tasksLeft = d && typeof d.tasks_left === 'number' ? d.tasks_left : 0;

      return {
        chart: {
          type: 'donut',
          toolbar: { show: false },
        },
        labels: STATUS_CHART_LABELS,
        colors: STATUS_CHART_COLORS,
        legend: {
          position: 'bottom',
          fontSize: '13px',
          markers: {
            width: 10,
            height: 10,
            radius: 3,
          },
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            fontWeight: 700,
            colors: ['#ffffff'],
          },
          dropShadow: {
            enabled: false,
          },
          formatter: function (val, opts) {
            var count = opts.w.globals.series[opts.seriesIndex];
            if (!count || count === 0) {
              return '';
            }
            return String(count);
          },
        },
        stroke: {
          width: 2,
          colors: ['#ffffff'],
        },
        plotOptions: {
          pie: {
            donut: {
              size: '72%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '13px',
                  color: '#6B7280',
                  offsetY: 20,
                },
                value: {
                  show: true,
                  fontSize: '28px',
                  fontWeight: 700,
                  color: '#111827',
                  offsetY: -8,
                  formatter: function () {
                    return String(tasksLeft);
                  },
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: 'Tasks left',
                  fontSize: '13px',
                  color: '#6B7280',
                  formatter: function () {
                    return String(tasksLeft);
                  },
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + ' task' + (val !== 1 ? 's' : '');
            },
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: {
                position: 'bottom',
              },
            },
          },
        ],
      };
    },
    priorityChartOptions() {
      return this.buildStackedStatusBarOptions(this.priorityChartCategories, this.priorityChartMaxY, '55%');
    },
    subBoardChartOptions() {
      var columnWidth = this.subBoardChartPageRows.length <= 3 ? '45%' : '55%';
      return this.buildStackedStatusBarOptions(this.subBoardChartCategories, this.subBoardChartMaxY, columnWidth);
    },
    memberChartOptions() {
      var count = this.memberChartPageRows.length;
      var columnWidth = '55%';
      if (count <= 4) {
        columnWidth = '40%';
      } else if (count <= 6) {
        columnWidth = '50%';
      }
      return this.buildStackedStatusBarOptions(this.memberChartCategories, this.memberChartMaxY, columnWidth);
    },
  },
  watch: {
    masterBoardId: {
      immediate: true,
      handler() {
        this.subBoardChartPage = 1;
        this.memberChartPage = 1;
        this.fetchChartStats();
      },
    },
    subBoardChartPageCount() {
      if (this.subBoardChartPage > this.subBoardChartPageCount && this.subBoardChartPageCount > 0) {
        this.subBoardChartPage = this.subBoardChartPageCount;
      }
    },
    memberChartPageCount() {
      if (this.memberChartPage > this.memberChartPageCount && this.memberChartPageCount > 0) {
        this.memberChartPage = this.memberChartPageCount;
      }
    },
  },
  methods: {
    buildStackedStatusSeries(rows) {
      if (!rows || !rows.length) {
        return [];
      }
      return [
        {
          name: 'Not Started',
          data: rows.map(function (row) { return row.not_started || 0; }),
        },
        {
          name: 'In Progress',
          data: rows.map(function (row) { return row.in_progress || 0; }),
        },
        {
          name: 'Late',
          data: rows.map(function (row) { return row.late || 0; }),
        },
        {
          name: 'Completed',
          data: rows.map(function (row) { return row.completed || 0; }),
        },
      ];
    },
    getStackedChartMaxY(rows) {
      var max = 0;
      var i;
      if (!rows || !rows.length) {
        return 10;
      }
      for (i = 0; i < rows.length; i++) {
        if (rows[i].total > max) {
          max = rows[i].total;
        }
      }
      if (max <= 0) {
        return 10;
      }
      return Math.ceil(max * 1.1);
    },
    buildStackedStatusBarOptions(categories, maxY, columnWidth) {
      return {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
        },
        colors: STATUS_CHART_COLORS,
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: columnWidth || '55%',
            borderRadius: 2,
            dataLabels: {
              total: {
                enabled: true,
                offsetY: -6,
                style: {
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#111827',
                },
                formatter: function (total) {
                  return total > 0 ? String(total) : '';
                },
              },
            },
          },
        },
        xaxis: {
          categories: categories,
          labels: {
            style: {
              fontSize: '11px',
            },
            rotate: categories.length > 3 ? -35 : 0,
            rotateAlways: categories.length > 3,
          },
        },
        yaxis: {
          min: 0,
          max: maxY,
          tickAmount: 5,
          labels: {
            formatter: function (val) {
              return Math.round(val);
            },
          },
        },
        legend: {
          position: 'bottom',
          fontSize: '13px',
          markers: {
            width: 10,
            height: 10,
            radius: 3,
          },
        },
        dataLabels: {
          enabled: false,
        },
        grid: {
          borderColor: '#f1f5f9',
          strokeDashArray: 4,
          padding: {
            top: 24,
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (val) {
              return val + ' task' + (val !== 1 ? 's' : '');
            },
          },
        },
      };
    },
    subBoardChartPrevPage() {
      if (this.subBoardChartPage > 1) {
        this.subBoardChartPage = this.subBoardChartPage - 1;
      }
    },
    subBoardChartNextPage() {
      if (this.subBoardChartPage < this.subBoardChartPageCount) {
        this.subBoardChartPage = this.subBoardChartPage + 1;
      }
    },
    memberChartPrevPage() {
      if (this.memberChartPage > 1) {
        this.memberChartPage = this.memberChartPage - 1;
      }
    },
    memberChartNextPage() {
      if (this.memberChartPage < this.memberChartPageCount) {
        this.memberChartPage = this.memberChartPage + 1;
      }
    },
    onStatusChartSegmentClick(event, chartContext, config) {
      var idx = config && typeof config.dataPointIndex === 'number' ? config.dataPointIndex : -1;
      if (idx < 0 || idx >= STATUS_CHART_BUCKETS.length) {
        return;
      }
      var count = this.statusChartSeries && this.statusChartSeries[idx] ? this.statusChartSeries[idx] : 0;
      if (!count) {
        return;
      }
      this.statusTasksModalBucket = STATUS_CHART_BUCKETS[idx];
      this.statusTasksModalLabel = STATUS_CHART_LABELS[idx];
      this.statusTasksModalVisible = true;
    },
    closeStatusTasksModal() {
      this.statusTasksModalVisible = false;
      this.statusTasksModalBucket = '';
      this.statusTasksModalLabel = '';
    },
    onPriorityChartBarClick(event, chartContext, config) {
      var idx = config && typeof config.dataPointIndex === 'number' ? config.dataPointIndex : -1;
      if (idx < 0) {
        return;
      }
      var rows = this.priorityChartRows;
      if (!rows || idx >= rows.length) {
        return;
      }
      var row = rows[idx];
      if (!row || !row.total) {
        return;
      }
      this.priorityTasksModalPriority = row.priority || '';
      this.priorityTasksModalLabel = row.label || row.priority || '';
      this.priorityTasksModalVisible = true;
    },
    closePriorityTasksModal() {
      this.priorityTasksModalVisible = false;
      this.priorityTasksModalPriority = '';
      this.priorityTasksModalLabel = '';
    },
    onSubBoardChartBarClick(event, chartContext, config) {
      var idx = config && typeof config.dataPointIndex === 'number' ? config.dataPointIndex : -1;
      if (idx < 0) {
        return;
      }
      var rows = this.subBoardChartPageRows;
      if (!rows || idx >= rows.length) {
        return;
      }
      var row = rows[idx];
      if (!row || !row.total) {
        return;
      }
      this.subBoardTasksModalBoardId = row.board_id || '';
      this.subBoardTasksModalLabel = row.label || 'Sub-board';
      this.subBoardTasksModalVisible = true;
    },
    closeSubBoardTasksModal() {
      this.subBoardTasksModalVisible = false;
      this.subBoardTasksModalBoardId = '';
      this.subBoardTasksModalLabel = '';
    },
    onMemberChartBarClick(event, chartContext, config) {
      var idx = config && typeof config.dataPointIndex === 'number' ? config.dataPointIndex : -1;
      if (idx < 0) {
        return;
      }
      var rows = this.memberChartPageRows;
      if (!rows || idx >= rows.length) {
        return;
      }
      var row = rows[idx];
      if (!row || !row.total) {
        return;
      }
      this.memberTasksModalUserId = row.user_id !== undefined && row.user_id !== null ? row.user_id : '';
      this.memberTasksModalLabel = row.label || 'Member';
      this.memberTasksModalVisible = true;
    },
    closeMemberTasksModal() {
      this.memberTasksModalVisible = false;
      this.memberTasksModalUserId = '';
      this.memberTasksModalLabel = '';
    },
    async fetchChartStats() {
      const id = this.masterBoardId;
      if (!id || parseInt(id, 10) === 0) {
        this.chartStats = null;
        return;
      }

      this.chartLoading = true;
      this.chartError = null;

      try {
        const result = await this.$apollo.query({
          query: gql`
            query GetTaskMasterBoardChartStats($master_board_id: ID!) {
              taskMasterBoardChartStats(master_board_id: $master_board_id) {
                error
                error_code
                message
                data {
                  status {
                    not_started
                    in_progress
                    late
                    completed
                    total
                    tasks_left
                  }
                  priority {
                    rows {
                      priority
                      label
                      not_started
                      in_progress
                      late
                      completed
                      total
                    }
                  }
                  sub_boards {
                    rows {
                      board_id
                      label
                      not_started
                      in_progress
                      late
                      completed
                      total
                    }
                  }
                  members {
                    rows {
                      user_id
                      label
                      not_started
                      in_progress
                      late
                      completed
                      total
                    }
                  }
                }
              }
            }
          `,
          variables: { master_board_id: String(id) },
          fetchPolicy: 'network-only',
        });

        const payload = result.data && result.data.taskMasterBoardChartStats;
        if (payload && !payload.error && payload.data) {
          this.chartStats = payload.data;
          this.subBoardChartPage = 1;
          this.memberChartPage = 1;
        } else {
          this.chartStats = null;
          this.chartError = (payload && payload.message) ? payload.message : 'Failed to load chart data.';
        }
      } catch (e) {
        console.error('Error fetching master board chart stats:', e);
        this.chartStats = null;
        this.chartError = 'Failed to load chart data.';
      } finally {
        this.chartLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.master-board-status-chart-wrap {
  margin-left: -8px;
  margin-right: -8px;
}

.master-board-status-chart-clickable,
.master-board-priority-chart-clickable,
.master-board-sub-board-chart-clickable,
.master-board-member-chart-clickable {
  cursor: pointer;
}
</style>
